import { useState, useEffect } from 'react';
import { Calendar, Check, X, Search, Filter } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import api from '../../services/api';

export default function StudentAttendance() {
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => { loadAttendance(); }, []);

  const loadAttendance = async () => {
    try {
      const res = await api.get('/student/attendance');
      if (res.data.success && res.data.data) {
        setAttendance(res.data.data.attendance || []);
      }
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const filteredAttendance = attendance.filter(a => {
    const matchesSearch = a.subject_name.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'present' && a.status?.toLowerCase() === 'present') ||
      (statusFilter === 'absent' && a.status?.toLowerCase() === 'absent');
    return matchesSearch && matchesStatus;
  });

  const presentCount = attendance.filter(a => a.status?.toLowerCase() === 'present').length;
  const absentCount = attendance.filter(a => a.status?.toLowerCase() === 'absent').length;
  const percentage = attendance.length > 0 ? Math.round((presentCount / attendance.length) * 100) : 0;

  const pieData = [
    { name: 'Present', value: presentCount, color: '#10b981' },
    { name: 'Absent', value: absentCount, color: '#ef4444' },
  ];

  const getAttendanceStatus = (pct) => {
    if (pct >= 90) return { label: 'Excellent', color: 'text-green-600' };
    if (pct >= 75) return { label: 'Good', color: 'text-blue-600' };
    if (pct >= 60) return { label: 'Average', color: 'text-yellow-600' };
    return { label: 'Poor', color: 'text-red-600' };
  };

  const status = getAttendanceStatus(percentage);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div></div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Attendance History</h1>
        <p className="text-sm sm:text-base text-gray-600">View your attendance records across all subjects</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 sm:p-5">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="p-1.5 sm:p-2 bg-indigo-100 rounded-lg">
              <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-600" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs sm:text-sm text-gray-500 truncate">Total Sessions</p>
              <p className="text-lg sm:text-2xl font-bold text-gray-800">{attendance.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 sm:p-5">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="p-1.5 sm:p-2 bg-green-100 rounded-lg">
              <Check className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs sm:text-sm text-gray-500 truncate">Present</p>
              <p className="text-lg sm:text-2xl font-bold text-green-600">{presentCount}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 sm:p-5">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="p-1.5 sm:p-2 bg-red-100 rounded-lg">
              <X className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs sm:text-sm text-gray-500 truncate">Absent</p>
              <p className="text-lg sm:text-2xl font-bold text-red-600">{absentCount}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 sm:p-5">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className={`p-1.5 sm:p-2 rounded-lg ${percentage >= 75 ? 'bg-green-100' : percentage >= 60 ? 'bg-yellow-100' : 'bg-red-100'}`}>
              <Calendar className={`w-4 h-4 sm:w-5 sm:h-5 ${percentage >= 75 ? 'text-green-600' : percentage >= 60 ? 'text-yellow-600' : 'text-red-600'}`} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs sm:text-sm text-gray-500 truncate">Percentage</p>
              <p className={`text-lg sm:text-2xl font-bold ${status.color}`}>{percentage}%</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Chart */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4">Attendance Overview</h3>
          {attendance.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={5} dataKey="value">
                    {pieData.map((entry, index) => (<Cell key={index} fill={entry.color} />))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <div className="text-center mt-4">
                <p className="text-2xl sm:text-3xl font-bold text-gray-800">{percentage}%</p>
                <p className={`text-xs sm:text-sm font-medium ${status.color}`}>{status.label} Attendance</p>
              </div>
              <div className="flex justify-center gap-4 sm:gap-6 mt-4">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  <span className="text-xs sm:text-sm text-gray-600">Present ({presentCount})</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-500"></div>
                  <span className="text-xs sm:text-sm text-gray-600">Absent ({absentCount})</span>
                </div>
              </div>
            </>
          ) : (
            <div className="h-[200px] flex items-center justify-center text-gray-400">No attendance data</div>
          )}
        </div>

        {/* Attendance List */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4">Attendance Records</h3>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input type="text" placeholder="Search by subject..." value={search} onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 text-sm" />
            </div>
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                className="pl-10 pr-8 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 text-sm appearance-none bg-white">
                <option value="all">All Status</option>
                <option value="present">Present Only</option>
                <option value="absent">Absent Only</option>
              </select>
            </div>
          </div>

          {filteredAttendance.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <Calendar className="w-10 h-10 mx-auto mb-2" />
              <p>No attendance records found</p>
            </div>
          ) : (
            <div className="max-h-[400px] overflow-y-auto">
              <table className="w-full">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden sm:table-cell">Subject</th>
                    <th className="px-2 sm:px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredAttendance.map((record, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="px-2 sm:px-4 py-3">
                        <div className="text-xs sm:text-sm font-medium text-gray-800">
                          {new Date(record.session_date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                        </div>
                        <div className="text-xs text-gray-500">
                          {new Date(record.session_date).getFullYear()}
                        </div>
                        <div className="text-xs text-gray-600 sm:hidden mt-1">{record.subject_name}</div>
                      </td>
                      <td className="px-2 sm:px-4 py-3 hidden sm:table-cell">
                        <div className="text-sm text-gray-800">{record.subject_name}</div>
                      </td>
                      <td className="px-2 sm:px-4 py-3 text-center">
                        {record.status?.toLowerCase() === 'present' ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                            <Check className="w-3 h-3" /> Present
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                            <X className="w-3 h-3" /> Absent
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Attendance Warning */}
      {percentage < 75 && attendance.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
          <div className="p-2 bg-red-100 rounded-lg shrink-0">
            <X className="w-5 h-5 text-red-600" />
          </div>
          <div>
            <h4 className="font-medium text-red-800">Low Attendance Warning</h4>
            <p className="text-sm text-red-600 mt-1">
              Your attendance is below 75%. Please ensure regular attendance to avoid any academic issues.
              You need to attend {Math.ceil((0.75 * attendance.length - presentCount) / 0.25)} more sessions to reach 75%.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
